import { useContext, useMemo } from "react";
import {
  Avatar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import useUserBase from "./hooks/useUserBase";
import AuthContext from "@/contexts/AuthContext";
import { PwaInstallProvider } from "@/core/pwa";

export default function UserBase({ size = "md" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { clubId: clubIdFromUrl } = useParams();
  const { userLoggedData, setSelectedClubId } = useContext(AuthContext);

  const resolvedClubId =
    clubIdFromUrl || userLoggedData?.selectedClubId || null;
  const resolvedClubName = useMemo(() => {
    const clubs = Array.isArray(userLoggedData?.clubs)
      ? userLoggedData.clubs
      : [];
    const selected =
      clubs.find((c) => c?.id === resolvedClubId) || clubs[0] || null;
    return selected?.name || "Clube";
  }, [userLoggedData?.clubs, resolvedClubId]);
  const selectedClub = useMemo(() => {
    const clubs = Array.isArray(userLoggedData?.clubs)
      ? userLoggedData.clubs
      : [];
    return clubs.find((c) => c?.id === resolvedClubId) || clubs[0] || null;
  }, [userLoggedData?.clubs, resolvedClubId]);

  const { refreshPlayerData } = useUserBase();

  const userName = userLoggedData?.name || "Jogador";
  const greetingName = userName.split(" ")[0];
  const avatarSrc =
    userLoggedData?.player?.avatarUrl || userLoggedData?.avatarUrl || "";

  const clubs = useMemo(
    () => (Array.isArray(userLoggedData?.clubs) ? userLoggedData.clubs : []),
    [userLoggedData?.clubs],
  );

  const currentSection = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    return lastSegment === "profile" ? "more" : lastSegment || "home";
  }, [location.pathname]);

  const navigationValue = useMemo(() => {
    switch (currentSection) {
      case "matches":
        return 1;
      case "ranking":
        return 2;
      case "more":
        return 3;
      case "home":
      default:
        return 0;
    }
  }, [currentSection]);

  const handleSelectClub = async (clubId) => {
    if (!clubId) return;
    await setSelectedClubId(clubId);
    navigate(`/c/${clubId}/user/${currentSection}`);
  };

  const handleChange = (event, newValue) => {
    refreshPlayerData();
    const basePath = resolvedClubId
      ? `/c/${resolvedClubId}/user`
      : "/private/user";
    switch (newValue) {
      case 0:
        navigate(`${basePath}/home`);
        break;
      case 1:
        navigate(`${basePath}/matches`);
        break;
      case 2:
        navigate(`${basePath}/ranking`);
        break;
      case 3:
        navigate(`${basePath}/${resolvedClubId ? "more" : "profile"}`);
        break;
      default:
        break;
    }
  };

  return (
    <PwaInstallProvider
      club={selectedClub}
      bottomOffset={84}
      hasReachedClubHome={currentSection === "home"}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
        <CssBaseline />

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          <Container maxWidth={size} sx={{ pt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                backgroundColor: "background.paper",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={avatarSrc || undefined}
                  alt={userName}
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "action.selected",
                    color: "text.secondary",
                    fontWeight: 700,
                  }}
                >
                  {!avatarSrc ? greetingName?.[0]?.toUpperCase() || "U" : null}
                </Avatar>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                  >
                    Olá, {greetingName}!
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Bem vindo ao {resolvedClubName}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 700, mb: 1.2 }}
              >
                Seus clubes
              </Typography>

              {clubs.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Nenhum clube disponível para este usuário.
                </Typography>
              ) : (
                <Grid container spacing={1.2} alignItems="stretch">
                  {clubs.map((club) => {
                    const isSelected = club?.id && club.id === resolvedClubId;
                    const logoSrc =
                      club?.logoLightUrl || club?.logoDarkUrl || null;

                    return (
                      <Grid
                        item
                        xs={clubs.length === 1 ? 12 : 6}
                        key={club?.id || club?.name}
                      >
                        <Paper
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectClub(club?.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleSelectClub(club?.id);
                            }
                          }}
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: isSelected
                              ? "primary.main"
                              : "divider",
                            bgcolor: isSelected
                              ? "action.selected"
                              : "background.default",
                            cursor: "pointer",
                            userSelect: "none",
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "center", sm: "center" },
                            gap: { xs: 0.9, sm: 1.1 },
                            width: "100%",
                            height: "100%",
                            minHeight: { xs: 92, sm: 64 },
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: "100%", sm: 92 },
                              height: { xs: 54, sm: 44 },
                              borderRadius: 1.25,
                              bgcolor: "action.hover",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {logoSrc ? (
                              <Box
                                component="img"
                                src={logoSrc}
                                alt={club?.name || "Clube"}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontWeight: 900,
                                  color: "text.secondary",
                                }}
                              >
                                {(club?.name?.[0] || "C").toUpperCase()}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              minWidth: 0,
                              textAlign: { xs: "center", sm: "left" },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 800 }}
                              noWrap
                            >
                              {club?.name || "Clube"}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>
          </Container>

          <Container maxWidth={size} sx={{ py: 2 }}>
            <Outlet />
          </Container>
        </Box>

        <BottomNavigation
          value={navigationValue}
          onChange={handleChange}
          showLabels
          sx={{
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            zIndex: 1000,
            height: 72,
            "& .MuiBottomNavigationAction-root": {
              py: 1.5,
              minWidth: 0,
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.75rem",
                mt: 0.5,
              },
              "& .MuiSvgIcon-root": {
                fontSize: 26,
              },
            },
          }}
        >
          <BottomNavigationAction label="Home" icon={<HomeIcon />} />
          <BottomNavigationAction
            label="Partidas"
            icon={<SportsBaseballIcon />}
          />
          <BottomNavigationAction label="Ranking" icon={<LeaderboardIcon />} />
          <BottomNavigationAction label="Mais" icon={<MoreHorizIcon />} />
        </BottomNavigation>
      </Box>
    </PwaInstallProvider>
  );
}
